import { useState, useEffect, useCallback, useRef } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { Badge } from 'src/@/components/ui/badge';
import Iconify from 'src/components/Iconify';
import actionListaTarefasPorChat from 'src/actions/tarefas/actionListaTarefasPorChat';
import tarefasService from 'src/services/tarefasService';
import { fDateTime, fDate } from 'src/utils/formatTime';

const getPriorityConfig = (t) => ({
    low: { label: t('chat.info.tasks.priority.low'), color: '#9ca3af', icon: 'mdi:arrow-down', className: 'bg-gray-50 text-gray-600 border-gray-200' },
    medium: { label: t('chat.info.tasks.priority.medium'), color: '#f59e0b', icon: 'mdi:minus', className: 'bg-amber-50 text-amber-700 border-amber-200' },
    high: { label: t('chat.info.tasks.priority.high'), color: '#f97316', icon: 'mdi:arrow-up', className: 'bg-orange-50 text-orange-700 border-orange-200' },
    urgent: { label: t('chat.info.tasks.priority.urgent'), color: '#ef4444', icon: 'mdi:alert-circle', className: 'bg-red-50 text-red-700 border-red-200' },
});

const fallbackStatusConfig = {
    'A Fazer': { className: 'bg-amber-50 text-amber-700 border-amber-200' },
    'Em Progresso': { className: 'bg-blue-50 text-blue-700 border-blue-200' },
    'Concluído': { className: 'bg-green-50 text-green-700 border-green-200' },
};

ChatInfoTarefas.propTypes = {
    chat: PropTypes.object.isRequired,
    user: PropTypes.object.isRequired,
    onOpenNovaTarefa: PropTypes.func.isRequired,
    onClickTarefa: PropTypes.func,
    onCountUpdate: PropTypes.func,
    refreshTrigger: PropTypes.number,
};

export default function ChatInfoTarefas({ chat, user, onOpenNovaTarefa, onClickTarefa, onCountUpdate, refreshTrigger }) {
    const { t } = useTranslation();
    const priorityConfig = getPriorityConfig(t);
    const [tarefas, setTarefas] = useState([]);
    const [loading, setLoading] = useState(false);
    const [showSearch, setShowSearch] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [searchResults, setSearchResults] = useState([]);
    const [searchLoading, setSearchLoading] = useState(false);
    const [vinculando, setVinculando] = useState(null);
    const debounceRef = useRef(null);

    const searchTarefas = useCallback((termo) => {
        if (debounceRef.current) clearTimeout(debounceRef.current);
        if (!termo || termo.length < 2) {
            setSearchResults([]);
            return;
        }
        debounceRef.current = setTimeout(async () => {
            setSearchLoading(true);
            try {
                const res = await tarefasService.listar(user?.clienteId, { search: termo, limit: 10 });
                const results = res.data || [];
                const tarefaIds = tarefas.map(t => t._id);
                setSearchResults(results.filter(r => !tarefaIds.includes(r._id)));
            } catch (error) {
                console.log('Erro ao pesquisar tarefas', error);
            } finally {
                setSearchLoading(false);
            }
        }, 300);
    }, [user?.clienteId, tarefas]);

    const handleVincularTarefa = async (tarefaId) => {
        setVinculando(tarefaId);
        try {
            await tarefasService.editar(tarefaId, { chat_id: chat._id });
            setSearchResults(prev => prev.filter(r => r._id !== tarefaId));
            setSearchTerm('');
            setShowSearch(false);
            buscarTarefas();
        } catch (error) {
            console.log('Erro ao vincular tarefa', error);
        } finally {
            setVinculando(null);
        }
    };

    async function buscarTarefas() {
        if (!chat?._id) return;
        setLoading(true);
        try {
            const response = await actionListaTarefasPorChat(chat._id);
            const data = response.data.data || [];
            setTarefas(data);
            if (onCountUpdate) onCountUpdate(data.length);
        } catch (error) {
            console.log('Erro ao buscar tarefas do chat', error);
        } finally {
            setLoading(false);
        }
    }

    useEffect(() => {
        buscarTarefas();
    }, [chat._id, refreshTrigger]);

    const getStatusBadge = (tarefa) => {
        const status = tarefa.status;
        const workflow = tarefa.workflow_id;

        // Try to get color from workflow stage
        if (workflow?.estagios) {
            const estagio = workflow.estagios.find(e => e.nome === status);
            if (estagio?.cor) {
                return (
                    <Badge
                        variant="outline"
                        className="text-[10px] font-medium border"
                        style={{
                            backgroundColor: `${estagio.cor}15`,
                            color: estagio.cor,
                            borderColor: `${estagio.cor}40`,
                        }}
                    >
                        {status}
                    </Badge>
                );
            }
        }

        // Fallback
        const config = fallbackStatusConfig[status] || fallbackStatusConfig['A Fazer'];
        return (
            <Badge variant="outline" className={`text-[10px] font-medium ${config.className}`}>
                {status}
            </Badge>
        );
    };

    const isOverdue = (tarefa) => {
        if (!tarefa.due_date) return false;
        if (tarefa.status === 'Concluído' || tarefa.status === 'done') return false;
        // Check if status is the last workflow stage (completed)
        if (tarefa.workflow_id?.estagios?.length > 0) {
            const sorted = [...tarefa.workflow_id.estagios].sort((a, b) => a.ordem - b.ordem);
            if (sorted[sorted.length - 1]?.nome === tarefa.status) return false;
        }
        const due = tarefa.due_date.slice(0, 10); // "YYYY-MM-DD"
        const today = new Date().toISOString().slice(0, 10);
        return due < today;
    };

    const getPriorityBadge = (priority) => {
        const config = priorityConfig[priority] || priorityConfig.medium;
        return (
            <Badge variant="outline" className={`text-[10px] font-medium ${config.className}`}>
                {config.label}
            </Badge>
        );
    };

    const getAssigneeName = (tarefa) => {
        const a = tarefa.assigned_to;
        if (!a) return null;
        if (typeof a === 'object') return a.nomeFantasia || a.nome || a.email || null;
        return null;
    };

    const getInitials = (name) => {
        if (!name) return '?';
        return name.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase();
    };

    const hashColor = (str) => {
        if (!str) return '#7C3AED';
        let hash = 0;
        for (let i = 0; i < str.length; i++) hash = str.charCodeAt(i) + ((hash << 5) - hash);
        const colors = ['#7C3AED', '#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#ec4899', '#06b6d4', '#8b5cf6'];
        return colors[Math.abs(hash) % colors.length];
    };

    const formatShortDate = (dateString) => {
        if (!dateString) return null;
        const d = new Date(dateString);
        const today = new Date();
        const tomorrow = new Date(today);
        tomorrow.setDate(tomorrow.getDate() + 1);
        if (d.toDateString() === today.toDateString()) return t('common.dates.today');
        if (d.toDateString() === tomorrow.toDateString()) return t('common.dates.tomorrow');
        return d.toLocaleDateString('pt-BR', { day: '2-digit', month: 'short' });
    };

    return (
        <div className="space-y-2">
            <div className="flex gap-2">
                <button
                    onClick={onOpenNovaTarefa}
                    className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 text-xs font-medium text-[#7C3AED] bg-violet-50 hover:bg-violet-100 rounded-lg transition-colors"
                >
                    <Iconify icon="eva:plus-outline" width={14} />
                    {t('common.buttons.create')}
                </button>
                <button
                    onClick={() => { setShowSearch(prev => !prev); setSearchTerm(''); setSearchResults([]); }}
                    className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-2 text-xs font-medium rounded-lg transition-colors ${showSearch ? 'text-white bg-[#7C3AED] hover:bg-[#6d28d9]' : 'text-[#7C3AED] bg-violet-50 hover:bg-violet-100'}`}
                >
                    <Iconify icon="eva:link-2-outline" width={14} />
                    {t('chat.info.tasks.link')}
                </button>
            </div>

            {showSearch && (
                <div className="space-y-2 p-2.5 bg-gray-50 dark:bg-gray-800/50 rounded-lg border border-gray-200 dark:border-gray-700 animate-in fade-in slide-in-from-top-1 duration-200">
                    <div className="relative">
                        <Iconify icon="eva:search-outline" width={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
                        <input
                            type="text"
                            placeholder={t('chat.info.tasks.searchPlaceholder')}
                            value={searchTerm}
                            onChange={(e) => { setSearchTerm(e.target.value); searchTarefas(e.target.value); }}
                            className="w-full pl-8 pr-3 py-1.5 text-xs border border-gray-200 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-200 focus:outline-none focus:border-[#7C3AED] focus:ring-1 focus:ring-[#7C3AED]/20"
                            autoFocus
                        />
                    </div>
                    {searchLoading && (
                        <p className="text-[10px] text-gray-400 text-center py-2">{t('chat.info.tasks.searching')}</p>
                    )}
                    {!searchLoading && searchTerm.length >= 2 && searchResults.length === 0 && (
                        <p className="text-[10px] text-gray-400 text-center py-2">{t('chat.info.tasks.noResults')}</p>
                    )}
                    {searchResults.length > 0 && (
                        <div className="max-h-[200px] overflow-y-auto space-y-1">
                            {searchResults.map((item) => (
                                <div
                                    key={item._id}
                                    onClick={() => !vinculando && handleVincularTarefa(item._id)}
                                    className="p-2 rounded-md hover:bg-violet-50 dark:hover:bg-violet-900/20 cursor-pointer transition-colors flex items-center justify-between gap-2"
                                >
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-1.5 flex-wrap">
                                            {getStatusBadge(item)}
                                            {getPriorityBadge(item.priority)}
                                        </div>
                                        <p className="text-xs text-gray-700 dark:text-gray-300 truncate mt-0.5">
                                            {item.title || t('chat.info.tasks.noTitle')}
                                        </p>
                                    </div>
                                    {vinculando === item._id ? (
                                        <Iconify icon="svg-spinners:ring-resize" width={14} className="text-[#7C3AED] flex-shrink-0" />
                                    ) : (
                                        <Iconify icon="eva:link-2-outline" width={14} className="text-gray-400 flex-shrink-0" />
                                    )}
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            )}

            {loading && tarefas.length === 0 && (
                <div className="text-center py-4">
                    <p className="text-xs text-gray-400">{t('common.buttons.loading')}</p>
                </div>
            )}

            {!loading && tarefas.length === 0 && (
                <div className="text-center py-4">
                    <div className="w-10 h-10 rounded-xl bg-gray-100 dark:bg-gray-800 flex items-center justify-center mx-auto mb-2">
                        <Iconify icon="eva:checkmark-circle-outline" width={20} className="text-gray-400" />
                    </div>
                    <p className="text-xs text-gray-500 dark:text-gray-400">{t('chat.info.tasks.empty')}</p>
                </div>
            )}

            {tarefas.length > 0 && (
                <div className="space-y-2">
                    {tarefas.map((tarefa) => {
                        const priority = priorityConfig[tarefa.priority] || priorityConfig.medium;
                        const overdue = isOverdue(tarefa);
                        const assignee = getAssigneeName(tarefa);
                        const avatarColor = hashColor(assignee || tarefa.assigned_to?._id);

                        return (
                            <div
                                key={tarefa._id}
                                onClick={() => onClickTarefa?.(tarefa)}
                                className="bg-white dark:bg-gray-800/50 border border-gray-100 dark:border-gray-700 rounded-xl hover:border-violet-200 dark:hover:border-violet-600 hover:shadow-sm cursor-pointer transition-all overflow-hidden"
                            >
                                {/* Color accent top */}
                                <div className="h-[3px] w-full" style={{ backgroundColor: overdue ? '#ef4444' : priority.color }} />

                                <div className="p-3 space-y-2">
                                    {/* Header: status + priority + overdue */}
                                    <div className="flex items-center justify-between">
                                        <div className="flex items-center gap-1.5 flex-wrap">
                                            {getStatusBadge(tarefa)}
                                            <span
                                                className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider"
                                                style={{ backgroundColor: `${priority.color}15`, color: priority.color }}
                                            >
                                                <Iconify icon={priority.icon} width={10} />
                                                {priority.label}
                                            </span>
                                        </div>
                                        {overdue && (
                                            <span className="text-[9px] font-bold text-red-500 bg-red-50 dark:bg-red-500/10 px-1.5 py-0.5 rounded flex items-center gap-0.5">
                                                <Iconify icon="mdi:alert-circle" width={10} />
                                                {t('chat.info.tasks.overdue')}
                                            </span>
                                        )}
                                    </div>

                                    {/* Title */}
                                    <p className="text-[13px] font-semibold text-gray-800 dark:text-gray-200 leading-snug line-clamp-2">
                                        {tarefa.title || t('chat.info.tasks.noTitle')}
                                    </p>

                                    {/* Info row: prazo + criação */}
                                    <div className="flex items-center gap-3">
                                        {tarefa.due_date && (
                                            <span className={`text-[10px] flex items-center gap-1 ${overdue ? 'text-red-500 font-semibold' : 'text-gray-500 dark:text-gray-400'}`}>
                                                <Iconify icon="mdi:calendar-clock-outline" width={12} />
                                                {formatShortDate(tarefa.due_date)}
                                            </span>
                                        )}
                                        {tarefa.created_at && (
                                            <span className="text-[10px] text-gray-400 dark:text-gray-500 flex items-center gap-1">
                                                <Iconify icon="mdi:clock-plus-outline" width={11} />
                                                {formatShortDate(tarefa.created_at)}
                                            </span>
                                        )}
                                    </div>

                                    {/* Footer: responsável */}
                                    <div className="flex items-center justify-between pt-1.5 border-t border-gray-100 dark:border-gray-700/50">
                                        {assignee ? (
                                            <div className="flex items-center gap-1.5 min-w-0">
                                                <div
                                                    className="w-5 h-5 rounded-full flex items-center justify-center text-white text-[8px] font-bold shrink-0"
                                                    style={{ backgroundColor: avatarColor }}
                                                >
                                                    {getInitials(assignee)}
                                                </div>
                                                <span className="text-[11px] font-medium text-gray-600 dark:text-gray-400 truncate">
                                                    {assignee}
                                                </span>
                                            </div>
                                        ) : (
                                            <div className="flex items-center gap-1.5">
                                                <div className="w-5 h-5 rounded-full bg-gray-100 dark:bg-gray-700 flex items-center justify-center border border-dashed border-gray-200 dark:border-gray-600">
                                                    <Iconify icon="mdi:account-plus-outline" width={10} className="text-gray-300 dark:text-gray-500" />
                                                </div>
                                                <span className="text-[10px] text-gray-400 italic">{t('chat.info.tasks.noAssignee')}</span>
                                            </div>
                                        )}
                                        {tarefa.checklist?.length > 0 && (
                                            <span className={`text-[10px] font-medium flex items-center gap-0.5 ${tarefa.checklist.filter(c => c.done).length === tarefa.checklist.length ? 'text-green-500' : 'text-gray-400'}`}>
                                                <Iconify icon="mdi:checkbox-marked-circle-outline" width={12} />
                                                {tarefa.checklist.filter(c => c.done).length}/{tarefa.checklist.length}
                                            </span>
                                        )}
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
}
